import { ChangeEvent } from 'react';
import { CoreCheckbox, CoreTooltip, CoreTypography } from '@youscience/core';

import { DEFAULT_CHECKPOINTS_LABELS } from '@constants/applyingSchoolsPlanner';

import { FormState } from '@interfaces/applications';

import useNotificationStore from '@store/NotificationStore';

import ActionButton from '@components/ActionButton';

import EditIcon from '@images/pencil.svg?react';
import DeleteCheckpointIcon from '@images/delete-checkpoint.svg?react';

import {
  StyledChecklist,
  StyledChecklistTitle,
  StyledChecklistItem,
  StyledChecklistItemGroup,
  StyledChecklistIconButton,
  StyledChecklistButtonGroup,
} from './PlannerSchoolCardChecklist.styles';

interface ApplicationChecklistProps {
  formState: FormState;
  schoolName: string;
  isEditApplication: boolean;
  isEditChecklist: boolean;
  setIsEditChecklist: (status: boolean) => void;
  onDefaultCheckBoxChange: (event: ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  onCustomCheckBoxChange: (event: ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  onDeleteDefaultCheckpoint: (key: string) => void;
  onDeleteCustomCheckpoint: (key: string) => void;
  onSave: () => void;
  onCancel: () => void;
}

export const PlannerSchoolCardChecklist = (props: ApplicationChecklistProps) => {
  const {
    formState,
    schoolName,
    isEditApplication,
    isEditChecklist,
    setIsEditChecklist,
    onDefaultCheckBoxChange,
    onCustomCheckBoxChange,
    onDeleteDefaultCheckpoint,
    onDeleteCustomCheckpoint,
    onSave,
    onCancel,
  } = props;

  const { defaultChecklist, customChecklist = [] } = formState;

  const { notify } = useNotificationStore((state) => state);

  const isChecklistEmpty = customChecklist.length === 0 && Object.entries(defaultChecklist ?? {}).length === 0;

  const handleOnSave = () => {
    setIsEditChecklist(false);
    onSave();

    notify({
      title: `Changes to the checklist for ${schoolName} saved`,
      message: '',
      severity: 'success',
    });
  };

  const handleOnCancel = () => {
    setIsEditChecklist(false);
    onCancel();
  };

  return (
    <StyledChecklist>
      <StyledChecklistTitle>
        <CoreTypography variant="subtitle2">Checklist</CoreTypography>

        {isEditApplication ? (
          <CoreTooltip title="Please save Application changes">
            <StyledChecklistIconButton isDisable={isEditApplication}>
              <EditIcon data-testid="disabledEditIcon" />
            </StyledChecklistIconButton>
          </CoreTooltip>
        ) : (
          <>
            {!isChecklistEmpty && !isEditChecklist ? (
              <StyledChecklistIconButton onClick={() => setIsEditChecklist(true)}>
                <EditIcon data-testid="editIcon" />
              </StyledChecklistIconButton>
            ) : null}
          </>
        )}

        {isEditChecklist && (
          <StyledChecklistButtonGroup>
            <ActionButton size="small" variant="outlined" data-testid="cancelBtn" onClick={handleOnCancel}>
              Cancel
            </ActionButton>

            <ActionButton data-testid="saveBtn" size="small" onClick={handleOnSave}>
              Save
            </ActionButton>
          </StyledChecklistButtonGroup>
        )}
      </StyledChecklistTitle>

      <StyledChecklistItemGroup>
        {defaultChecklist &&
          Object.entries(defaultChecklist).map(([key, value]) => (
            <StyledChecklistItem key={key}>
              <CoreCheckbox
                data-testid="checklistCheckbox"
                checked={value}
                disabled={isEditChecklist}
                value={key}
                onChange={onDefaultCheckBoxChange}
              />

              <CoreTypography>{(DEFAULT_CHECKPOINTS_LABELS as Record<string, unknown>)[key] as string}</CoreTypography>

              {isEditChecklist && (
                <StyledChecklistIconButton data-testid="deleteChecklistIcon">
                  <DeleteCheckpointIcon onClick={() => onDeleteDefaultCheckpoint(key)} />
                </StyledChecklistIconButton>
              )}
            </StyledChecklistItem>
          ))}

        {customChecklist &&
          customChecklist.map((item) => (
            <StyledChecklistItem key={item.label}>
              <CoreCheckbox
                data-testid="checklistCheckbox"
                checked={item.value}
                value={item.label}
                disabled={isEditChecklist}
                onChange={onCustomCheckBoxChange}
              />

              <CoreTypography>{item.label}</CoreTypography>

              {isEditChecklist && (
                <StyledChecklistIconButton>
                  <DeleteCheckpointIcon onClick={() => onDeleteCustomCheckpoint(item.label)} />
                </StyledChecklistIconButton>
              )}
            </StyledChecklistItem>
          ))}

        {isChecklistEmpty ? <CoreTypography>Add your to do-list here</CoreTypography> : null}
      </StyledChecklistItemGroup>
    </StyledChecklist>
  );
};
